// All CEO Reports Tab Components
// This file contains all tab components for the CEO Reports page
// Organized for maintainability while keeping related tabs together

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, Target,
  CheckCircle, AlertTriangle, BookOpen, Building2, Zap,
  Calendar, Filter, ArrowUpRight, ArrowDownRight, ChevronRight,
  Activity, Globe, BarChart3, AlertCircle, Shield, Briefcase,
  Banknote, Landmark
} from 'lucide-react';
import { formatCurrency, formatPercent } from './utils/formatters';
import ReportInfo from './components/ReportInfo';


// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "primary" }) => (
  <Card className="glass border-white/10">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-${color}/10`}>
          <Icon className={`w-6 h-6 text-${color}`} />
        </div>
      </div>
      {trend && (
        <div className={`flex items-center mt-2 text-sm ${trendValue >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {trendValue >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
          {formatPercent(trendValue)} vs last month
        </div>
      )}
    </CardContent>
  </Card>
);

// Executive Summary Tab
const ExecutiveSummaryTab = ({ data, loading, glossary }) => {
  if (loading) return <div className="text-center py-8">Loading executive summary...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  return (
    <div className="space-y-6">
      {/* Header with Methodology Button */}
      <div className="flex items-center justify-between">
        <div className={`p-3 rounded-lg flex items-center gap-2 flex-1 ${data.mt5_connected ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
          {data.mt5_connected ? <CheckCircle className="w-5 h-5 text-emerald-400" /> : <AlertTriangle className="w-5 h-5 text-amber-400" />}
          <span className="text-sm">
            Data Source: <strong>{data.data_source}</strong> | Last Updated: {new Date(data.report_timestamp).toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={data.mt5_connected ? "default" : "secondary"}>
            {data.mt5_connected ? "Live Data" : "Simulated"}
          </Badge>
          <ReportInfo reportKey="executive-summary" glossary={glossary} />
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard 
          title="Assets Under Management" 
          value={formatCurrency(data.total_aum)} 
          icon={DollarSign}
          trend={true}
          trendValue={data.aum_growth_mom}
          color="cyan"
        />
        <MetricCard 
          title="Monthly Revenue" 
          value={formatCurrency(data.monthly_revenue)} 
          icon={TrendingUp}
          trend={true}
          trendValue={data.revenue_growth_mom}
          color="emerald"
        />
        <MetricCard 
          title="Net Profit" 
          value={formatCurrency(data.net_profit)} 
          subtitle={`${data.profit_margin.toFixed(1)}% margin`}
          icon={Target}
          color={data.net_profit >= 0 ? "emerald" : "red"}
        />
        <MetricCard 
          title="Total Clients" 
          value={data.total_clients} 
          subtitle={`${data.active_traders} active traders`}
          icon={Users}
          trend={true}
          trendValue={data.client_growth_mom}
          color="blue"
        />
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Financial Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Monthly Revenue</span>
                <span className="font-semibold text-emerald-400">{formatCurrency(data.monthly_revenue)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Monthly Expenses</span>
                <span className="font-semibold text-red-400">-{formatCurrency(data.monthly_expenses)}</span>
              </div>
              <hr className="border-white/10" />
              <div className="flex justify-between items-center">
                <span className="font-semibold">Net Profit</span>
                <span className={`font-bold text-lg ${data.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(data.net_profit)}
                </span>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Profit Margin</span>
                  <span>{data.profit_margin.toFixed(1)}%</span>
                </div>
                <Progress value={Math.max(0, data.profit_margin)} className="h-2" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Risk Indicators</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Total Exposure</span>
                <span className="font-semibold">{formatCurrency(data.total_exposure)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Margin Utilization</span>
                <span className="font-semibold">{data.margin_utilization.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Largest Position</span>
                <span className="font-semibold">{data.largest_position_pct.toFixed(1)}% of AUM</span>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Margin Utilization</span>
                  <span>{data.margin_utilization.toFixed(1)}%</span>
                </div>
                <Progress 
                  value={Math.min(100, data.margin_utilization)} 
                  className={`h-2 ${data.margin_utilization > 80 ? 'bg-red-500' : data.margin_utilization > 50 ? 'bg-amber-500' : ''}`}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Financial Statement Tab
const FinancialStatementTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading financial statement...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Breakdown */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-emerald-400">Revenue Breakdown</CardTitle>
            <CardDescription>
              Period: {new Date(data.period_start).toLocaleDateString()} - {new Date(data.period_end).toLocaleDateString()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { label: 'Trading Commission', value: data.trading_commission },
                { label: 'Spread Markup', value: data.spread_markup },
                { label: 'Swap Income', value: data.swap_income },
                { label: 'Deposit Fees', value: data.deposit_fees },
                { label: 'Withdrawal Fees', value: data.withdrawal_fees },
                { label: 'Other Income', value: data.other_income },
              ].map((item, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="text-emerald-400">{formatCurrency(item.value, 2)}</span>
                </div>
              ))}
              <hr className="border-white/10" />
              <div className="flex justify-between items-center font-bold">
                <span>Total Revenue</span>
                <span className="text-emerald-400 text-lg">{formatCurrency(data.total_revenue, 2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expenses Breakdown */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-red-400">Expenses Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { label: 'MT5 License Fees', value: data.mt5_license_fees },
                { label: 'Payment Gateway Fees', value: data.payment_gateway_fees },
                { label: 'Marketing Costs', value: data.marketing_costs },
                { label: 'Operational Costs', value: data.operational_costs },
                { label: 'Staff Costs', value: data.staff_costs },
                { label: 'Technology Costs', value: data.technology_costs },
                { label: 'Regulatory Fees', value: data.regulatory_fees },
                { label: 'Other Expenses', value: data.other_expenses },
              ].map((item, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="text-red-400">-{formatCurrency(item.value, 2)}</span>
                </div>
              ))}
              <hr className="border-white/10" />
              <div className="flex justify-between items-center font-bold">
                <span>Total Expenses</span>
                <span className="text-red-400 text-lg">-{formatCurrency(data.total_expenses, 2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profitability Summary */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Profitability Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Gross Profit</p>
              <p className="text-xl font-bold text-emerald-400">{formatCurrency(data.gross_profit)}</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Operating Profit</p>
              <p className="text-xl font-bold text-emerald-400">{formatCurrency(data.operating_profit)}</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Net Profit</p>
              <p className={`text-xl font-bold ${data.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {formatCurrency(data.net_profit)}
              </p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">EBITDA</p>
              <p className="text-xl font-bold text-cyan-400">{formatCurrency(data.ebitda)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Balance Sheet Tab
const BalanceSheetTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading balance sheet...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  return (
    <div className="space-y-6">
      <div className="text-sm text-muted-foreground">
        As of: {new Date(data.as_of_date).toLocaleDateString()}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assets */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-emerald-400">Assets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { label: 'Cash & Equivalents', value: data.cash_and_equivalents },
                { label: 'Client Funds (Segregated)', value: data.client_funds_segregated },
                { label: 'Accounts Receivable', value: data.accounts_receivable },
                { label: 'Prepaid Expenses', value: data.prepaid_expenses },
                { label: 'Technology Assets', value: data.technology_assets },
                { label: 'Other Assets', value: data.other_assets },
              ].map((item, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span>{formatCurrency(item.value, 2)}</span>
                </div>
              ))}
              <hr className="border-white/10" />
              <div className="flex justify-between items-center font-bold">
                <span>Total Assets</span>
                <span className="text-emerald-400">{formatCurrency(data.total_assets, 2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Liabilities */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-red-400">Liabilities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { label: 'Client Deposits', value: data.client_deposits },
                { label: 'Accounts Payable', value: data.accounts_payable },
                { label: 'Accrued Expenses', value: data.accrued_expenses },
                { label: 'Deferred Revenue', value: data.deferred_revenue },
                { label: 'Regulatory Reserves', value: data.regulatory_reserves },
                { label: 'Other Liabilities', value: data.other_liabilities },
              ].map((item, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span>{formatCurrency(item.value, 2)}</span>
                </div>
              ))}
              <hr className="border-white/10" />
              <div className="flex justify-between items-center font-bold">
                <span>Total Liabilities</span>
                <span className="text-red-400">{formatCurrency(data.total_liabilities, 2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Equity */}
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-cyan-400">Equity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { label: 'Share Capital', value: data.share_capital },
                { label: 'Retained Earnings', value: data.retained_earnings },
                { label: 'Current Period Profit', value: data.current_period_profit },
              ].map((item, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span>{formatCurrency(item.value, 2)}</span>
                </div>
              ))}
              <hr className="border-white/10" />
              <div className="flex justify-between items-center font-bold">
                <span>Total Equity</span>
                <span className="text-cyan-400">{formatCurrency(data.total_equity, 2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Ratios */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Financial Ratios</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Current Ratio</p>
              <p className="text-2xl font-bold">{data.current_ratio.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">{data.current_ratio >= 1.5 ? 'Healthy' : 'Monitor'}</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Debt to Equity</p>
              <p className="text-2xl font-bold">{data.debt_to_equity.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">{data.debt_to_equity <= 2 ? 'Acceptable' : 'High'}</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Working Capital</p>
              <p className={`text-2xl font-bold ${data.working_capital >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {formatCurrency(data.working_capital)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Client Analytics Tab
const ClientAnalyticsTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading client analytics...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard title="Total Clients" value={data.total_clients} icon={Users} />
        <MetricCard title="Active (30d)" value={data.active_clients_30d} icon={Activity} color="emerald" />
        <MetricCard title="New MTD" value={data.new_clients_mtd} icon={TrendingUp} color="cyan" />
        <MetricCard title="Churned MTD" value={data.churned_clients_mtd} icon={TrendingDown} color="red" />
        <MetricCard title="Dormant" value={data.dormant_clients} icon={AlertTriangle} color="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Client Value Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Average Client Value</span>
                <span className="font-semibold">{formatCurrency(data.average_client_value)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Top 10 Clients AUM</span>
                <span className="font-semibold">{formatCurrency(data.top_10_clients_aum)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Top 10 % of Total</span>
                <span className="font-semibold">{data.top_10_clients_pct.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Est. Lifetime Value</span>
                <span className="font-semibold text-cyan-400">{formatCurrency(data.client_lifetime_value)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Geographic Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(data.clients_by_country).map(([country, count]) => (
                <div key={country} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <span>{country}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold">{count} clients</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      ({formatCurrency(data.aum_by_country[country] || 0)})
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">By Leverage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(data.clients_by_leverage).map(([leverage, count]) => (
                <div key={leverage} className="flex justify-between">
                  <span>1:{leverage}</span>
                  <span className="font-semibold">{count} clients</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">By Account Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(data.clients_by_account_type).map(([type, count]) => (
                <div key={type} className="flex justify-between">
                  <span>{type}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Trading Analytics Tab
const TradingAnalyticsTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading trading analytics...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  return (
    <div className="space-y-6">
      {/* Volume Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard 
          title="Total Volume MTD" 
          value={formatCurrency(data.total_volume_mtd)} 
          icon={Activity}
          color="cyan"
        />
        <MetricCard 
          title="Total Trades MTD" 
          value={data.total_trades_mtd.toLocaleString()} 
          icon={BarChart3}
          color="blue"
        />
        <MetricCard 
          title="Avg Trade Size" 
          value={formatCurrency(data.average_trade_size)} 
          icon={Target}
          color="emerald"
        />
      </div>

      {/* Revenue from Trading */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-emerald-400">Trading Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Commission MTD</span>
                <span className="font-semibold text-emerald-400">{formatCurrency(data.commission_mtd, 2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Spread Revenue MTD</span>
                <span className="font-semibold text-emerald-400">{formatCurrency(data.spread_revenue_mtd, 2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Swap Revenue MTD</span>
                <span className="font-semibold text-emerald-400">{formatCurrency(data.swap_revenue_mtd, 2)}</span>
              </div>
              <hr className="border-white/10" />
              <div className="flex justify-between items-center font-bold">
                <span>Total Trading Revenue</span>
                <span className="text-emerald-400 text-lg">
                  {formatCurrency(data.commission_mtd + data.spread_revenue_mtd + data.swap_revenue_mtd, 2)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Performance Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Winning Trades %</span>
                <span className="font-semibold">{data.winning_trades_pct.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Avg Profit/Trade</span>
                <span className="font-semibold">{formatCurrency(data.average_profit_per_trade, 2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Client P&L MTD</span>
                <span className={`font-semibold ${data.total_client_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(data.total_client_pnl, 2)}
                </span>
              </div>
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Win Rate</span>
                  <span>{data.winning_trades_pct.toFixed(1)}%</span>
                </div>
                <Progress value={data.winning_trades_pct} className="h-2" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Volume by Symbol */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Volume by Symbol</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(data.volume_by_symbol).map(([symbol, volume]) => (
              <div key={symbol} className="p-4 rounded-lg bg-white/5 text-center">
                <p className="text-sm text-muted-foreground">{symbol}</p>
                <p className="text-lg font-bold text-cyan-400">{formatCurrency(volume)}</p>
                <p className="text-xs text-muted-foreground">{data.trades_by_symbol[symbol]} trades</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Book Analysis Tab (A/B/C Books)
const BookAnalysisTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading book analysis...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  const getBookColor = (book) => {
    switch (book) {
      case 'A': return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
      case 'B': return 'text-amber-400 bg-amber-500/10 border-amber-500/30';
      case 'C': return 'text-purple-400 bg-purple-500/10 border-purple-500/30';
      default: return 'text-gray-400 bg-gray-500/10';
    }
  };

  return (
    <div className="space-y-6">
      {/* Net Book P&L Banner */}
      <div className={`p-4 rounded-lg ${data.net_book_pnl >= 0 ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className={`w-8 h-8 ${data.net_book_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
            <div>
              <p className="text-lg font-bold">Net Book P&L: {formatCurrency(data.net_book_pnl)}</p>
              <p className="text-sm opacity-80">Total Revenue: {formatCurrency(data.total_revenue)} | Total Liability: {formatCurrency(data.total_liability)}</p>
            </div>
          </div>
          <Badge variant={data.net_book_pnl >= 0 ? "default" : "destructive"} className="text-lg px-4 py-2">
            {data.net_book_pnl >= 0 ? 'Profitable' : 'At Risk'}
          </Badge>
        </div>
      </div>

      {/* Book Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* A-Book */}
        <Card className={`glass border ${getBookColor('A')}`}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="text-2xl font-bold text-blue-400">A</span>
              <span>Book (STP/ECN)</span>
            </CardTitle>
            <CardDescription>Passed to liquidity providers - Zero risk</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Volume</span>
                <span className="font-semibold">{formatCurrency(data.a_book_volume)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Revenue</span>
                <span className="font-semibold text-emerald-400">{formatCurrency(data.a_book_revenue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Liability</span>
                <span className="font-semibold text-muted-foreground">{formatCurrency(data.a_book_liability)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Clients</span>
                <span className="font-semibold">{data.a_book_clients}</span>
              </div>
              <hr className="border-white/10" />
              <div className="text-center">
                <span className="text-2xl font-bold text-blue-400">{data.a_book_pct.toFixed(1)}%</span>
                <p className="text-xs text-muted-foreground">of total volume</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* B-Book */}
        <Card className={`glass border ${getBookColor('B')}`}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="text-2xl font-bold text-amber-400">B</span>
              <span>Book (Market Maker)</span>
            </CardTitle>
            <CardDescription>Broker takes opposite side - High risk/reward</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Volume</span>
                <span className="font-semibold">{formatCurrency(data.b_book_volume)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Revenue</span>
                <span className="font-semibold text-emerald-400">{formatCurrency(data.b_book_revenue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Liability</span>
                <span className={`font-semibold ${data.b_book_liability > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                  {formatCurrency(data.b_book_liability)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Clients</span>
                <span className="font-semibold">{data.b_book_clients}</span>
              </div>
              <hr className="border-white/10" />
              <div className="text-center">
                <span className="text-2xl font-bold text-amber-400">{data.b_book_pct.toFixed(1)}%</span>
                <p className="text-xs text-muted-foreground">of total volume</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* C-Book */}
        <Card className={`glass border ${getBookColor('C')}`}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="text-2xl font-bold text-purple-400">C</span>
              <span>Book (Hybrid)</span>
            </CardTitle>
            <CardDescription>Partial hedging - Balanced risk</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Volume</span>
                <span className="font-semibold">{formatCurrency(data.c_book_volume)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Revenue</span>
                <span className="font-semibold text-emerald-400">{formatCurrency(data.c_book_revenue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Liability</span>
                <span className={`font-semibold ${data.c_book_liability > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                  {formatCurrency(data.c_book_liability)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Clients</span>
                <span className="font-semibold">{data.c_book_clients}</span>
              </div>
              <hr className="border-white/10" />
              <div className="text-center">
                <span className="text-2xl font-bold text-purple-400">{data.c_book_pct.toFixed(1)}%</span>
                <p className="text-xs text-muted-foreground">of total volume</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Book Distribution Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>A-Book (Safe)</span>
                <span>{data.a_book_pct.toFixed(1)}%</span>
              </div>
              <Progress value={data.a_book_pct} className="h-3 bg-blue-500/20" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>B-Book (Risk)</span>
                <span>{data.b_book_pct.toFixed(1)}%</span>
              </div>
              <Progress value={data.b_book_pct} className="h-3 bg-amber-500/20" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>C-Book (Hybrid)</span>
                <span>{data.c_book_pct.toFixed(1)}%</span>
              </div>
              <Progress value={data.c_book_pct} className="h-3 bg-purple-500/20" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Margin Calls Tab
const MarginCallsTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading margin call data...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  const getRiskBadge = (level) => {
    switch (level) {
      case 'STOP_OUT': return <Badge variant="destructive">STOP OUT</Badge>;
      case 'CRITICAL': return <Badge className="bg-red-500/80">CRITICAL</Badge>;
      case 'WARNING': return <Badge className="bg-amber-500/80">WARNING</Badge>;
      default: return <Badge variant="secondary">SAFE</Badge>;
    }
  };

  const getBookBadge = (book) => {
    const colors = {
      'A': 'bg-blue-500/20 text-blue-400',
      'B': 'bg-amber-500/20 text-amber-400',
      'C': 'bg-purple-500/20 text-purple-400'
    };
    return <Badge className={colors[book]}>{book}-Book</Badge>;
  };

  const renderAccountTable = (accounts, title, bgClass) => {
    if (accounts.length === 0) return null;
    
    return (
      <Card className={`glass border-white/10 ${bgClass}`}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span>{title}</span>
            <Badge variant="outline">{accounts.length} accounts</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b border-white/10">
                  <th className="text-left py-2">Account</th>
                  <th className="text-right py-2">Balance</th>
                  <th className="text-right py-2">Equity</th>
                  <th className="text-right py-2">Margin Level</th>
                  <th className="text-right py-2">P&L</th>
                  <th className="text-center py-2">Book</th>
                  <th className="text-center py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.login} className="border-b border-white/5">
                    <td className="py-3">
                      <div>
                        <p className="font-medium">{account.name}</p>
                        <p className="text-xs text-muted-foreground">#{account.login}</p>
                      </div>
                    </td>
                    <td className="text-right">{formatCurrency(account.balance)}</td>
                    <td className="text-right">{formatCurrency(account.equity)}</td>
                    <td className="text-right">
                      <span className={account.margin_level < 100 ? 'text-red-400' : account.margin_level < 150 ? 'text-amber-400' : ''}>
                        {account.margin_level.toFixed(1)}%
                      </span>
                    </td>
                    <td className={`text-right ${account.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatCurrency(account.profit)}
                    </td>
                    <td className="text-center">{getBookBadge(account.book_type)}</td>
                    <td className="text-center">{getRiskBadge(account.risk_level)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary Banner */}
      <div className={`p-4 rounded-lg ${data.total_at_risk_count > 0 ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <AlertCircle className={`w-8 h-8 ${data.total_at_risk_count > 0 ? 'text-amber-400' : 'text-emerald-400'}`} />
            <div>
              <p className="text-lg font-bold">
                {data.total_at_risk_count} Accounts Approaching Margin Call
              </p>
              <p className="text-sm opacity-80">
                Total Equity at Risk: {formatCurrency(data.total_at_risk_equity)}
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Potential Revenue</p>
              <p className="text-lg font-bold text-emerald-400">{formatCurrency(data.potential_revenue_on_stopout)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Potential Liability</p>
              <p className="text-lg font-bold text-red-400">{formatCurrency(data.potential_liability_on_stopout)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Distribution by Book */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass border-white/10 border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">A-Book at Risk</p>
                <p className="text-2xl font-bold">{data.a_book_at_risk}</p>
              </div>
              <Badge className="bg-blue-500/20 text-blue-400">Low Impact</Badge>
            </div>
          </CardContent>
        </Card>
        <Card className="glass border-white/10 border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">B-Book at Risk</p>
                <p className="text-2xl font-bold">{data.b_book_at_risk}</p>
              </div>
              <Badge className="bg-amber-500/20 text-amber-400">Revenue Potential</Badge>
            </div>
          </CardContent>
        </Card>
        <Card className="glass border-white/10 border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">C-Book at Risk</p>
                <p className="text-2xl font-bold">{data.c_book_at_risk}</p>
              </div>
              <Badge className="bg-purple-500/20 text-purple-400">Partial Impact</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account Tables */}
      {renderAccountTable(data.stop_out_accounts, "🔴 Stop Out Risk (< 50%)", "border-l-4 border-l-red-500")}
      {renderAccountTable(data.critical_accounts, "🟠 Critical (50-100%)", "border-l-4 border-l-orange-500")}
      {renderAccountTable(data.warning_accounts, "🟡 Warning (100-150%)", "border-l-4 border-l-amber-500")}

      {data.total_at_risk_count === 0 && (
        <Card className="glass border-white/10 border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-emerald-400 mb-4" />
            <h3 className="text-xl font-bold mb-2">All Clear</h3>
            <p className="text-muted-foreground">No accounts are currently approaching margin call levels.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Risk Report Tab
const RiskReportTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading risk report...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  const getRiskColor = (score) => {
    switch (score) {
      case 'LOW': return 'text-emerald-400 bg-emerald-500/10';
      case 'MEDIUM': return 'text-amber-400 bg-amber-500/10';
      case 'HIGH': return 'text-red-400 bg-red-500/10';
      default: return 'text-gray-400 bg-gray-500/10';
    }
  };

  return (
    <div className="space-y-6">
      {/* Risk Score Banner */}
      <div className={`p-4 rounded-lg ${getRiskColor(data.risk_score)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <div>
              <p className="text-lg font-bold">Overall Risk Score: {data.risk_score}</p>
              <p className="text-sm opacity-80">Based on exposure, concentration, and margin metrics</p>
            </div>
          </div>
          <Badge variant="outline" className="text-lg px-4 py-2">{data.risk_score}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Exposure Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Client Exposure</span>
                <span className="font-semibold">{formatCurrency(data.total_client_exposure)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Largest Single Exposure</span>
                <span className="font-semibold">{formatCurrency(data.largest_single_exposure)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Concentration Risk</span>
                <span className="font-semibold">{data.concentration_risk_score.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">VaR (95%)</span>
                <span className="font-semibold text-amber-400">{formatCurrency(data.var_95)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Margin Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Margin Used</span>
                <span className="font-semibold">{formatCurrency(data.total_margin_used)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Free Margin</span>
                <span className="font-semibold text-emerald-400">{formatCurrency(data.total_free_margin)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Margin Call Accounts</span>
                <span className={`font-semibold ${data.margin_call_accounts > 0 ? 'text-amber-400' : ''}`}>
                  {data.margin_call_accounts}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Stop Out Accounts</span>
                <span className={`font-semibold ${data.stop_out_accounts > 0 ? 'text-red-400' : ''}`}>
                  {data.stop_out_accounts}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exposure by Symbol */}
      {Object.keys(data.net_exposure_by_symbol).length > 0 && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Net Exposure by Symbol</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(data.net_exposure_by_symbol).map(([symbol, exposure]) => (
                <div key={symbol} className="p-3 rounded-lg bg-white/5 text-center">
                  <p className="text-sm text-muted-foreground">{symbol}</p>
                  <p className={`text-lg font-bold ${exposure >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrency(exposure)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regulatory Metrics */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Regulatory Compliance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Capital Adequacy</p>
              <p className={`text-2xl font-bold ${data.capital_adequacy_ratio >= 100 ? 'text-emerald-400' : 'text-red-400'}`}>
                {data.capital_adequacy_ratio.toFixed(1)}%
              </p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Segregated Funds</p>
              <p className="text-2xl font-bold text-emerald-400">{data.segregated_funds_ratio.toFixed(1)}%</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Max Drawdown</p>
              <p className="text-2xl font-bold text-amber-400">{data.max_drawdown_client.toFixed(1)}%</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Risk Score</p>
              <p className={`text-2xl font-bold ${getRiskColor(data.risk_score).split(' ')[0]}`}>{data.risk_score}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Investor Report Tab
const InvestorReportTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading investor report...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  // Safe number formatting helper
  const safeToFixed = (value, decimals = 1) => {
    if (value === undefined || value === null || isNaN(value)) return '0';
    return Number(value).toFixed(decimals);
  };

  return (
    <div className="space-y-6">
      {/* Company Header */}
      <Card className="glass border-white/10 bg-gradient-to-r from-cyan-500/10 to-blue-500/10">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{data.company_name || 'TD-Capital'}</h2>
              <p className="text-muted-foreground">Investment Due Diligence Report</p>
              <div className="flex gap-2 mt-2">
                <Badge variant="outline">{data.regulatory_status || 'Regulated'}</Badge>
                {(data.licenses || []).map((lic, idx) => (
                  <Badge key={idx} variant="secondary">{lic}</Badge>
                ))}
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Report Date</p>
              <p className="font-semibold">{data.report_date || new Date().toLocaleDateString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Valuation Section */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Valuation Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-6 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30">
              <p className="text-sm text-muted-foreground">Implied Valuation</p>
              <p className="text-3xl font-bold text-cyan-400">{formatCurrency(data.implied_valuation || 0)}</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Revenue Multiple</p>
              <p className="text-2xl font-bold">{data.revenue_multiple || 0}x</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">Profit Multiple</p>
              <p className="text-2xl font-bold">{data.profit_multiple || 0}x</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-white/5">
              <p className="text-sm text-muted-foreground">AUM Multiple</p>
              <p className="text-2xl font-bold">{safeToFixed((data.aum_multiple || 0) * 100, 0)}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TTM Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Trailing 12 Months Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">TTM Revenue</span>
                <span className="font-semibold text-emerald-400">{formatCurrency(data.ttm_revenue || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">TTM Net Profit</span>
                <span className={`font-semibold ${(data.ttm_net_profit || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(data.ttm_net_profit || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Client Growth</span>
                <span className="font-semibold">{safeToFixed(data.ttm_client_growth)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Volume Growth</span>
                <span className="font-semibold">{safeToFixed(data.ttm_volume_growth)}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Revenue Projections</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Year 1</span>
                <span className="font-semibold">{formatCurrency(data.projected_revenue_y1 || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Year 2</span>
                <span className="font-semibold">{formatCurrency(data.projected_revenue_y2 || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Year 3</span>
                <span className="font-semibold">{formatCurrency(data.projected_revenue_y3 || 0)}</span>
              </div>
              <hr className="border-white/10" />
              <div className="flex justify-between">
                <span className="font-semibold">Projected CAGR</span>
                <span className="font-bold text-cyan-400">{safeToFixed(data.cagr_projection)}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Strengths & Risks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass border-white/10 border-l-4 border-l-emerald-500">
          <CardHeader>
            <CardTitle className="text-lg text-emerald-400">Key Strengths</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {(data.strengths || []).map((strength, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="glass border-white/10 border-l-4 border-l-amber-500">
          <CardHeader>
            <CardTitle className="text-lg text-amber-400">Risk Factors</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {(data.risk_factors || []).map((risk, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>{risk}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Technology Summary */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Technology Infrastructure</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">{data.technology_summary || 'Modern cloud-based infrastructure with MT5 integration.'}</p>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== NEW TAB COMPONENTS ====================

// P&L by Symbol Tab
const PnLBySymbolTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading P&L by symbol...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard title="Total Volume" value={formatCurrency(data.total_volume)} icon={Activity} color="cyan" />
        <MetricCard title="Total Trades" value={data.total_trades?.toLocaleString()} icon={BarChart3} color="blue" />
        <MetricCard title="Net P&L" value={formatCurrency(data.total_net_pnl)} icon={DollarSign} color={data.total_net_pnl >= 0 ? "emerald" : "red"} />
        <MetricCard title="Total Commission" value={formatCurrency(data.total_commission)} icon={Banknote} color="amber" />
      </div>

      {/* Symbol Table */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">P&L Breakdown by Symbol</CardTitle>
          <CardDescription>Real-time profit/loss per trading instrument</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b border-white/10">
                  <th className="text-left py-3">Symbol</th>
                  <th className="text-right py-3">Volume</th>
                  <th className="text-right py-3">Trades</th>
                  <th className="text-right py-3">Gross P&L</th>
                  <th className="text-right py-3">Commission</th>
                  <th className="text-right py-3">Swap</th>
                  <th className="text-right py-3">Net P&L</th>
                  <th className="text-right py-3">Win Rate</th>
                </tr>
              </thead>
              <tbody>
                {(data.symbols || []).map((s) => (
                  <tr key={s.symbol} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 font-medium">{s.symbol}</td>
                    <td className="text-right">{formatCurrency(s.volume)}</td>
                    <td className="text-right">{s.trades}</td>
                    <td className={`text-right ${s.gross_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(s.gross_pnl, 2)}</td>
                    <td className="text-right text-amber-400">{formatCurrency(s.commission, 2)}</td>
                    <td className={`text-right ${s.swap >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(s.swap, 2)}</td>
                    <td className={`text-right font-semibold ${s.net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(s.net_pnl, 2)}</td>
                    <td className="text-right">{s.win_rate?.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top/Bottom Symbols */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="glass border-white/10 border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Top Profitable Symbol</p>
            <p className="text-xl font-bold text-emerald-400">{data.top_profitable_symbol}</p>
          </CardContent>
        </Card>
        <Card className="glass border-white/10 border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Top Losing Symbol</p>
            <p className="text-xl font-bold text-red-400">{data.top_losing_symbol}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Client Profitability Tab
const ClientProfitabilityTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading client profitability...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  const renderClientTable = (clients, title, isProfit) => (
    <Card className={`glass border-white/10 border-l-4 ${isProfit ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
      <CardHeader>
        <CardTitle className={`text-lg ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b border-white/10">
                <th className="text-left py-2">Client</th>
                <th className="text-center py-2">Book</th>
                <th className="text-right py-2">Trading P&L</th>
                <th className="text-right py-2">LTV</th>
              </tr>
            </thead>
            <tbody>
              {(clients || []).map((c) => (
                <tr key={c.login} className="border-b border-white/5">
                  <td className="py-2">
                    <p className="font-medium">{c.name}</p>
                    <p className="text-xs text-muted-foreground">#{c.login}</p>
                  </td>
                  <td className="text-center">
                    <Badge className={c.book_type === 'A' ? 'bg-blue-500/20' : c.book_type === 'B' ? 'bg-amber-500/20' : 'bg-purple-500/20'}>
                      {c.book_type}
                    </Badge>
                  </td>
                  <td className={`text-right ${c.trading_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrency(c.trading_pnl, 2)}
                  </td>
                  <td className="text-right font-semibold">{formatCurrency(c.lifetime_value, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard title="Total Client P&L" value={formatCurrency(data.total_client_pnl)} icon={Users} color={data.total_client_pnl >= 0 ? "emerald" : "red"} />
        <MetricCard title="B-Book Revenue" value={formatCurrency(data.broker_revenue_from_losses)} subtitle="From client losses" icon={DollarSign} color="amber" />
        <MetricCard title="Profitable Clients" value={`${data.profitable_client_pct?.toFixed(1)}%`} icon={TrendingUp} color="emerald" />
        <MetricCard title="Avg Client LTV" value={formatCurrency(data.avg_client_ltv)} icon={Target} color="cyan" />
      </div>

      {/* Client Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderClientTable(data.top_profitable_clients, "Top Profitable Clients", true)}
        {renderClientTable(data.top_losing_clients, "Top Losing Clients", false)}
      </div>
    </div>
  );
};

// Commission Analytics Tab
const CommissionAnalyticsTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading commission analytics...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass border-white/10">
          <CardHeader><CardTitle className="text-lg">Daily</CardTitle></CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(data.daily_commission + data.daily_spread_revenue)}</p>
            <p className="text-sm text-muted-foreground">Commission: {formatCurrency(data.daily_commission)}</p>
            <p className="text-sm text-muted-foreground">Spread: {formatCurrency(data.daily_spread_revenue)}</p>
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardHeader><CardTitle className="text-lg">Weekly</CardTitle></CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(data.weekly_commission + data.weekly_spread_revenue)}</p>
            <div className="flex items-center gap-1 text-sm mt-1">
              <span className={data.commission_trend_wow >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                {formatPercent(data.commission_trend_wow)} WoW
              </span>
            </div>
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardHeader><CardTitle className="text-lg">Monthly</CardTitle></CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(data.monthly_commission + data.monthly_spread_revenue)}</p>
            <div className="flex items-center gap-1 text-sm mt-1">
              <span className={data.commission_trend_mom >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                {formatPercent(data.commission_trend_mom)} MoM
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* By Symbol */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass border-white/10">
          <CardHeader><CardTitle className="text-lg">Commission by Symbol</CardTitle></CardHeader>
          <CardContent>
            {Object.entries(data.commission_by_symbol || {}).map(([symbol, value]) => (
              <div key={symbol} className="flex justify-between py-2 border-b border-white/5">
                <span>{symbol}</span>
                <span className="font-semibold">{formatCurrency(value)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardHeader><CardTitle className="text-lg">Spread Revenue by Symbol</CardTitle></CardHeader>
          <CardContent>
            {Object.entries(data.spread_by_symbol || {}).map(([symbol, value]) => (
              <div key={symbol} className="flex justify-between py-2 border-b border-white/5">
                <span>{symbol}</span>
                <span className="font-semibold">{formatCurrency(value)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Forecast */}
      <Card className="glass border-white/10 bg-gradient-to-r from-cyan-500/10 to-blue-500/10">
        <CardHeader><CardTitle className="text-lg">Revenue Forecast</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Projected Monthly</p>
              <p className="text-xl font-bold">{formatCurrency(data.projected_monthly_commission)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Projected Quarterly</p>
              <p className="text-xl font-bold">{formatCurrency(data.projected_quarterly_commission)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Deposit Flow Tab
const DepositFlowTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading deposit/withdrawal data...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass border-white/10 border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Monthly Deposits</p>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(data.monthly_deposits)}</p>
            <p className="text-xs text-muted-foreground">{data.deposit_count} transactions</p>
          </CardContent>
        </Card>
        <Card className="glass border-white/10 border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Monthly Withdrawals</p>
            <p className="text-2xl font-bold text-red-400">{formatCurrency(data.monthly_withdrawals)}</p>
            <p className="text-xs text-muted-foreground">{data.withdrawal_count} transactions</p>
          </CardContent>
        </Card>
        <Card className="glass border-white/10 border-l-4 border-l-cyan-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Net Flow</p>
            <p className={`text-2xl font-bold ${data.monthly_net_flow >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {formatCurrency(data.monthly_net_flow)}
            </p>
            <p className="text-xs text-muted-foreground">Withdrawal rate: {data.withdrawal_rate?.toFixed(1)}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Payment Methods */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass border-white/10">
          <CardHeader><CardTitle className="text-lg text-emerald-400">Deposits by Method</CardTitle></CardHeader>
          <CardContent>
            {Object.entries(data.deposits_by_method || {}).map(([method, value]) => (
              <div key={method} className="flex justify-between py-2 border-b border-white/5">
                <span>{method}</span>
                <span className="font-semibold text-emerald-400">{formatCurrency(value)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardHeader><CardTitle className="text-lg text-red-400">Withdrawals by Method</CardTitle></CardHeader>
          <CardContent>
            {Object.entries(data.withdrawals_by_method || {}).map(([method, value]) => (
              <div key={method} className="flex justify-between py-2 border-b border-white/5">
                <span>{method}</span>
                <span className="font-semibold text-red-400">{formatCurrency(value)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Risk Indicators */}
      <Card className="glass border-white/10 border-amber-500/30 bg-amber-500/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <span>Churn Risk Clients: <strong>{data.churn_risk_clients}</strong></span>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Pending Withdrawals: </span>
              <span className="font-semibold text-amber-400">{formatCurrency(data.pending_withdrawals)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Hedging Efficiency Tab
const HedgingEfficiencyTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading hedging efficiency...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  const getEfficiencyColor = (score) => {
    if (score === 'OPTIMAL') return 'text-emerald-400 bg-emerald-500/10';
    if (score === 'UNDER_HEDGED') return 'text-amber-400 bg-amber-500/10';
    return 'text-red-400 bg-red-500/10';
  };

  return (
    <div className="space-y-6">
      {/* Efficiency Score */}
      <Card className={`glass border-white/10 ${getEfficiencyColor(data.efficiency_score)}`}>
        <CardContent className="p-6 text-center">
          <p className="text-sm uppercase tracking-wider mb-2">Hedging Efficiency</p>
          <p className="text-3xl font-bold">{data.efficiency_score?.replace('_', ' ')}</p>
          <p className="text-sm mt-2">Current Hedge Ratio: {data.overall_hedge_ratio?.toFixed(1)}% | Optimal: {data.optimal_hedge_ratio}%</p>
        </CardContent>
      </Card>

      {/* Book Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="glass border-white/10 border-l-4 border-l-blue-500">
          <CardHeader><CardTitle className="text-lg text-blue-400">A-Book (STP)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between"><span>Volume</span><span>{formatCurrency(data.a_book_volume)}</span></div>
            <div className="flex justify-between"><span>Hedged</span><span>{data.a_book_hedged_pct}%</span></div>
            <div className="flex justify-between"><span>Slippage</span><span className="text-red-400">{formatCurrency(data.a_book_slippage)}</span></div>
            <div className="flex justify-between"><span>Execution Cost</span><span>{formatCurrency(data.a_book_execution_cost)}</span></div>
            <div className="flex justify-between"><span>LP Rebates</span><span className="text-emerald-400">{formatCurrency(data.a_book_lp_rebates)}</span></div>
            <hr className="border-white/10" />
            <div className="flex justify-between font-semibold"><span>Net Cost</span><span className="text-red-400">{formatCurrency(data.a_book_net_cost)}</span></div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10 border-l-4 border-l-amber-500">
          <CardHeader><CardTitle className="text-lg text-amber-400">B-Book (MM)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between"><span>Volume</span><span>{formatCurrency(data.b_book_volume)}</span></div>
            <div className="flex justify-between"><span>Client Losses</span><span className="text-emerald-400">{formatCurrency(data.b_book_client_losses)}</span></div>
            <div className="flex justify-between"><span>Client Wins</span><span className="text-red-400">{formatCurrency(data.b_book_client_wins)}</span></div>
            <div className="flex justify-between"><span>Risk Exposure</span><span className="text-amber-400">{formatCurrency(data.b_book_risk_exposure)}</span></div>
            <hr className="border-white/10" />
            <div className="flex justify-between font-semibold"><span>Net Revenue</span><span className="text-emerald-400">{formatCurrency(data.b_book_net_revenue)}</span></div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10 border-l-4 border-l-purple-500">
          <CardHeader><CardTitle className="text-lg text-purple-400">C-Book (Hybrid)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between"><span>Volume</span><span>{formatCurrency(data.c_book_volume)}</span></div>
            <div className="flex justify-between"><span>Hedged</span><span>{data.c_book_hedged_pct}%</span></div>
            <hr className="border-white/10" />
            <div className="flex justify-between font-semibold"><span>Net P&L</span><span className={data.c_book_net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>{formatCurrency(data.c_book_net_pnl)}</span></div>
          </CardContent>
        </Card>
      </div>

      {/* Metrics */}
      <Card className="glass border-white/10">
        <CardHeader><CardTitle className="text-lg">Performance Metrics</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm text-muted-foreground">Hedging Cost %</p>
              <p className="text-xl font-bold">{data.hedging_cost_pct?.toFixed(3)}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Risk-Adjusted Return</p>
              <p className="text-xl font-bold">{data.risk_adjusted_return?.toFixed(2)} bps</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Overall Hedge Ratio</p>
              <p className="text-xl font-bold">{data.overall_hedge_ratio?.toFixed(1)}%</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Regulatory Capital Tab
const RegulatoryCapitalTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading regulatory data...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  const getStatusBadge = (status) => {
    if (status === 'COMPLIANT') return <Badge className="bg-emerald-500">COMPLIANT</Badge>;
    if (status === 'WARNING') return <Badge className="bg-amber-500">WARNING</Badge>;
    return <Badge variant="destructive">NON-COMPLIANT</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Overall Status */}
      <Card className={`glass border-white/10 ${data.overall_compliance_status === 'COMPLIANT' ? 'bg-emerald-500/10' : data.overall_compliance_status === 'WARNING' ? 'bg-amber-500/10' : 'bg-red-500/10'}`}>
        <CardContent className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Landmark className="w-8 h-8" />
            <div>
              <p className="text-lg font-bold">Regulatory Compliance Status</p>
              <p className="text-sm text-muted-foreground">Bodies: {(data.regulatory_bodies || []).join(', ')}</p>
            </div>
          </div>
          {getStatusBadge(data.overall_compliance_status)}
        </CardContent>
      </Card>

      {/* Compliance Areas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={`glass border-white/10 ${data.capital_compliant ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-red-500'}`}>
          <CardHeader><CardTitle className="text-lg">Capital Adequacy</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between"><span>Required</span><span>{formatCurrency(data.minimum_capital_required)}</span></div>
              <div className="flex justify-between"><span>Current</span><span className="text-emerald-400">{formatCurrency(data.current_capital)}</span></div>
              <div className="flex justify-between"><span>Surplus</span><span>{formatCurrency(data.capital_surplus)}</span></div>
              <hr className="border-white/10" />
              <div className="flex justify-between font-semibold"><span>CAR</span><span>{data.capital_adequacy_ratio?.toFixed(1)}%</span></div>
            </div>
          </CardContent>
        </Card>

        <Card className={`glass border-white/10 ${data.leverage_compliant ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-red-500'}`}>
          <CardHeader><CardTitle className="text-lg">Leverage Limits</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between"><span>Max Allowed</span><span>1:{data.max_leverage_allowed}</span></div>
              <div className="flex justify-between"><span>Current Avg</span><span>1:{data.current_avg_leverage?.toFixed(0)}</span></div>
              <div className="flex justify-between"><span>Exceeding</span><span className={data.clients_exceeding_leverage > 0 ? 'text-amber-400' : ''}>{data.clients_exceeding_leverage} clients</span></div>
              <hr className="border-white/10" />
              <div className="flex justify-between font-semibold"><span>Compliance</span><span>{data.leverage_compliance_pct?.toFixed(1)}%</span></div>
            </div>
          </CardContent>
        </Card>

        <Card className={`glass border-white/10 ${data.segregation_compliant ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-red-500'}`}>
          <CardHeader><CardTitle className="text-lg">Fund Segregation</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between"><span>Client Funds</span><span>{formatCurrency(data.client_funds_held)}</span></div>
              <div className="flex justify-between"><span>Segregated</span><span className="text-emerald-400">{formatCurrency(data.segregated_funds)}</span></div>
              <hr className="border-white/10" />
              <div className="flex justify-between font-semibold"><span>Ratio</span><span>{data.segregation_ratio?.toFixed(1)}%</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Audit Info */}
      <Card className="glass border-white/10">
        <CardContent className="p-4 flex justify-between items-center">
          <div>
            <p className="text-sm text-muted-foreground">Last Audit</p>
            <p className="font-semibold">{data.last_audit_date}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Next Audit</p>
            <p className="font-semibold">{data.next_audit_date}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Suspicious Activity Tab
const SuspiciousActivityTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading AML alerts...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  const getSeverityBadge = (severity) => {
    const colors = { CRITICAL: 'bg-red-600', HIGH: 'bg-red-500', MEDIUM: 'bg-amber-500', LOW: 'bg-blue-500' };
    return <Badge className={colors[severity]}>{severity}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Alert Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="glass border-white/10"><CardContent className="p-4 text-center"><p className="text-sm text-muted-foreground">Total</p><p className="text-2xl font-bold">{data.total_alerts}</p></CardContent></Card>
        <Card className="glass border-white/10 border-l-4 border-l-red-600"><CardContent className="p-4 text-center"><p className="text-sm text-muted-foreground">Critical</p><p className="text-2xl font-bold text-red-400">{data.critical_alerts}</p></CardContent></Card>
        <Card className="glass border-white/10 border-l-4 border-l-red-500"><CardContent className="p-4 text-center"><p className="text-sm text-muted-foreground">High</p><p className="text-2xl font-bold text-red-400">{data.high_alerts}</p></CardContent></Card>
        <Card className="glass border-white/10 border-l-4 border-l-amber-500"><CardContent className="p-4 text-center"><p className="text-sm text-muted-foreground">Medium</p><p className="text-2xl font-bold text-amber-400">{data.medium_alerts}</p></CardContent></Card>
        <Card className="glass border-white/10 border-l-4 border-l-blue-500"><CardContent className="p-4 text-center"><p className="text-sm text-muted-foreground">Low</p><p className="text-2xl font-bold text-blue-400">{data.low_alerts}</p></CardContent></Card>
      </div>

      {/* Recent Alerts */}
      <Card className="glass border-white/10">
        <CardHeader><CardTitle className="text-lg">Recent Alerts</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(data.recent_alerts || []).map((alert) => (
              <div key={alert.alert_id} className="p-4 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-muted-foreground">{alert.alert_id}</span>
                    {getSeverityBadge(alert.severity)}
                    <Badge variant="outline">{alert.alert_type?.replace('_', ' ')}</Badge>
                  </div>
                  <Badge variant={alert.status === 'RESOLVED' ? 'secondary' : 'default'}>{alert.status}</Badge>
                </div>
                <p className="text-sm">{alert.description}</p>
                <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                  <span>Client: {alert.client_name} (#{alert.client_login})</span>
                  <span>Amount: {formatCurrency(alert.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AML Metrics */}
      <Card className="glass border-white/10">
        <CardHeader><CardTitle className="text-lg">AML Monitoring</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <p className="text-sm text-muted-foreground">Large Deposits Today</p>
              <p className="text-xl font-bold">{data.large_deposits_today}</p>
              <p className="text-xs text-muted-foreground">Threshold: {formatCurrency(data.large_deposit_threshold)}</p>
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <p className="text-sm text-muted-foreground">PEP Clients</p>
              <p className="text-xl font-bold">{data.pep_clients}</p>
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <p className="text-sm text-muted-foreground">High-Risk Countries</p>
              <p className="text-xl font-bold">{data.high_risk_jurisdictions}</p>
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <p className="text-sm text-muted-foreground">Pending KYC</p>
              <p className="text-xl font-bold text-amber-400">{data.pending_kyc_reviews}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Execution Quality Tab
const ExecutionQualityTab = ({ data, loading }) => {
  if (loading) return <div className="text-center py-8">Loading execution quality...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  return (
    <div className="space-y-6">
      {/* Quality Score */}
      <Card className={`glass border-white/10 ${data.quality_rating === 'EXCELLENT' ? 'bg-emerald-500/10' : data.quality_rating === 'GOOD' ? 'bg-cyan-500/10' : 'bg-amber-500/10'}`}>
        <CardContent className="p-6 text-center">
          <p className="text-sm uppercase tracking-wider mb-2">Execution Quality Score</p>
          <p className="text-4xl font-bold">{data.execution_quality_score?.toFixed(0)}/100</p>
          <Badge className={data.quality_rating === 'EXCELLENT' ? 'bg-emerald-500' : data.quality_rating === 'GOOD' ? 'bg-cyan-500' : 'bg-amber-500'}>{data.quality_rating}</Badge>
        </CardContent>
      </Card>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass border-white/10">
          <CardHeader><CardTitle className="text-lg">Execution Speed</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between"><span>Average</span><span>{data.avg_execution_time_ms?.toFixed(1)} ms</span></div>
            <div className="flex justify-between"><span>P95</span><span>{data.p95_execution_time_ms?.toFixed(1)} ms</span></div>
            <div className="flex justify-between"><span>P99</span><span>{data.p99_execution_time_ms?.toFixed(1)} ms</span></div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardHeader><CardTitle className="text-lg">Slippage</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between"><span>Avg Slippage</span><span>{data.avg_slippage_pips?.toFixed(2)} pips</span></div>
            <div className="flex justify-between"><span>Positive</span><span className="text-emerald-400">{data.positive_slippage_pct?.toFixed(1)}%</span></div>
            <div className="flex justify-between"><span>Negative</span><span className="text-red-400">{data.negative_slippage_pct?.toFixed(1)}%</span></div>
            <div className="flex justify-between"><span>Zero</span><span>{data.zero_slippage_pct?.toFixed(1)}%</span></div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardHeader><CardTitle className="text-lg">Order Quality</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between"><span>Total Orders</span><span>{data.total_orders?.toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Requote Rate</span><span className="text-amber-400">{data.requote_rate?.toFixed(2)}%</span></div>
            <div className="flex justify-between"><span>Rejection Rate</span><span className="text-red-400">{data.rejection_rate?.toFixed(3)}%</span></div>
            <div className="flex justify-between"><span>Full Fill</span><span className="text-emerald-400">{data.full_fill_pct?.toFixed(1)}%</span></div>
          </CardContent>
        </Card>
      </div>

      {/* By Symbol */}
      <Card className="glass border-white/10">
        <CardHeader><CardTitle className="text-lg">Execution by Symbol</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b border-white/10">
                  <th className="text-left py-2">Symbol</th>
                  <th className="text-right py-2">Avg Exec (ms)</th>
                  <th className="text-right py-2">Slippage (pips)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.execution_by_symbol || {}).map(([symbol, metrics]) => (
                  <tr key={symbol} className="border-b border-white/5">
                    <td className="py-2 font-medium">{symbol}</td>
                    <td className="text-right">{metrics.avg_ms?.toFixed(1)}</td>
                    <td className={`text-right ${metrics.slippage >= 0 ? 'text-red-400' : 'text-emerald-400'}`}>{metrics.slippage?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Daily Funding Tab with Date Picker and Group Filtering
const DailyFundingTab = ({ data, loading, glossary, onDateChange, onGroupFilter, selectedDate, selectedGroups }) => {
  const [localDate, setLocalDate] = useState(selectedDate || new Date().toISOString().split('T')[0]);
  const [localGroups, setLocalGroups] = useState(selectedGroups || '');
  const [viewMode, setViewMode] = useState('processor'); // 'processor' or 'group'

  const handleDateChange = (e) => {
    setLocalDate(e.target.value);
    if (onDateChange) onDateChange(e.target.value);
  };

  const handleGroupFilter = () => {
    if (onGroupFilter) onGroupFilter(localGroups);
  };

  if (loading) return <div className="text-center py-8">Loading daily funding report...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  const getActivityBadge = (level) => {
    const colors = {
      'INACTIVE': 'bg-gray-500/20 text-gray-400',
      'LOW': 'bg-blue-500/20 text-blue-400',
      'NORMAL': 'bg-emerald-500/20 text-emerald-400',
      'HIGH': 'bg-amber-500/20 text-amber-400',
      'VERY_HIGH': 'bg-red-500/20 text-red-400'
    };
    return <Badge className={colors[level] || 'bg-gray-500/20'}>{level?.replace('_', ' ')}</Badge>;
  };

  const getGroupTypeBadge = (type) => {
    const colors = {
      'RETAIL': 'bg-blue-500/20 text-blue-400',
      'VIP': 'bg-purple-500/20 text-purple-400',
      'ECN': 'bg-cyan-500/20 text-cyan-400',
      'IB': 'bg-amber-500/20 text-amber-400',
      'DEMO': 'bg-gray-500/20 text-gray-400'
    };
    return <Badge className={colors[type] || 'bg-gray-500/20'}>{type}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header with Date Picker and Filters */}
      <Card className="glass border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-medium">Business Date:</span>
                <input
                  type="date"
                  value={localDate}
                  onChange={handleDateChange}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Filter groups (comma-separated)"
                  value={localGroups}
                  onChange={(e) => setLocalGroups(e.target.value)}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm w-64"
                />
                <Button size="sm" variant="outline" onClick={handleGroupFilter}>Apply</Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant={viewMode === 'processor' ? 'default' : 'outline'}
                onClick={() => setViewMode('processor')}
              >
                By Processor
              </Button>
              <Button 
                size="sm" 
                variant={viewMode === 'group' ? 'default' : 'outline'}
                onClick={() => setViewMode('group')}
              >
                By MT5 Group
              </Button>
              <ReportInfo reportKey="daily-funding" glossary={glossary} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Consolidated Totals */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="glass border-white/10 border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Deposits</p>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(data.total_deposits)}</p>
            <p className="text-xs text-muted-foreground">{data.total_deposit_count} transactions</p>
            <div className="flex items-center gap-1 mt-1">
              {data.deposit_change_pct >= 0 ? (
                <ArrowUpRight className="w-3 h-3 text-emerald-400" />
              ) : (
                <ArrowDownRight className="w-3 h-3 text-red-400" />
              )}
              <span className={`text-xs ${data.deposit_change_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {data.deposit_change_pct >= 0 ? '+' : ''}{data.deposit_change_pct}% vs yesterday
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10 border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Withdrawals</p>
            <p className="text-2xl font-bold text-red-400">{formatCurrency(data.total_withdrawals)}</p>
            <p className="text-xs text-muted-foreground">{data.total_withdrawal_count} transactions</p>
            <div className="flex items-center gap-1 mt-1">
              {data.withdrawal_change_pct >= 0 ? (
                <ArrowUpRight className="w-3 h-3 text-red-400" />
              ) : (
                <ArrowDownRight className="w-3 h-3 text-emerald-400" />
              )}
              <span className={`text-xs ${data.withdrawal_change_pct <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {data.withdrawal_change_pct >= 0 ? '+' : ''}{data.withdrawal_change_pct}% vs yesterday
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className={`glass border-white/10 border-l-4 ${data.total_net_flow >= 0 ? 'border-l-cyan-500' : 'border-l-amber-500'}`}>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Net Flow</p>
            <p className={`text-2xl font-bold ${data.total_net_flow >= 0 ? 'text-cyan-400' : 'text-amber-400'}`}>
              {formatCurrency(data.total_net_flow)}
            </p>
            <p className="text-xs text-muted-foreground">
              Change: {formatCurrency(data.net_flow_change)}
            </p>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Avg Deposit</p>
            <p className="text-2xl font-bold">{formatCurrency(data.avg_deposit_size)}</p>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Avg Withdrawal</p>
            <p className="text-2xl font-bold">{formatCurrency(data.avg_withdrawal_size)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Activity Level Summary */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">MT5 Group Activity Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4 text-center">
            <div className="p-3 rounded-lg bg-gray-500/10">
              <p className="text-2xl font-bold text-gray-400">{data.inactive_groups}</p>
              <p className="text-xs text-muted-foreground">Inactive</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-500/10">
              <p className="text-2xl font-bold text-blue-400">{data.low_activity_groups}</p>
              <p className="text-xs text-muted-foreground">Low Activity</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-500/10">
              <p className="text-2xl font-bold text-emerald-400">{data.normal_activity_groups}</p>
              <p className="text-xs text-muted-foreground">Normal</p>
            </div>
            <div className="p-3 rounded-lg bg-amber-500/10">
              <p className="text-2xl font-bold text-amber-400">{data.high_activity_groups}</p>
              <p className="text-xs text-muted-foreground">High Activity</p>
            </div>
            <div className="p-3 rounded-lg bg-red-500/10">
              <p className="text-2xl font-bold text-red-400">{data.very_high_activity_groups}</p>
              <p className="text-xs text-muted-foreground">Very High</p>
            </div>
          </div>

          {/* Alerts */}
          {(data.unusually_active_groups?.length > 0 || data.inactive_group_names?.length > 0) && (
            <div className="mt-4 space-y-2">
              {data.unusually_active_groups?.length > 0 && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-sm font-medium text-red-400">Unusually Active Groups:</span>
                    <span className="text-sm">{data.unusually_active_groups.join(', ')}</span>
                  </div>
                </div>
              )}
              {data.inactive_group_names?.length > 0 && (
                <div className="p-3 rounded-lg bg-gray-500/10 border border-gray-500/30">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-400">Inactive Groups:</span>
                    <span className="text-sm">{data.inactive_group_names.join(', ')}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Processor View */}
      {viewMode === 'processor' && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Breakdown by Payment Processor</CardTitle>
            <CardDescription>Funding activity by payment method for {data.business_date}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b border-white/10">
                    <th className="text-left py-3">Processor</th>
                    <th className="text-right py-3">Deposits</th>
                    <th className="text-right py-3">Dep Count</th>
                    <th className="text-right py-3">Withdrawals</th>
                    <th className="text-right py-3">Wit Count</th>
                    <th className="text-right py-3">Net Flow</th>
                    <th className="text-right py-3">Avg Dep</th>
                    <th className="text-right py-3">Est. Fees</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_processor || []).map((proc) => (
                    <tr key={proc.processor_name} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 font-medium">{proc.processor_name}</td>
                      <td className="text-right text-emerald-400">{formatCurrency(proc.deposit_amount)}</td>
                      <td className="text-right">{proc.deposit_count}</td>
                      <td className="text-right text-red-400">{formatCurrency(proc.withdrawal_amount)}</td>
                      <td className="text-right">{proc.withdrawal_count}</td>
                      <td className={`text-right font-semibold ${proc.net_flow >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(proc.net_flow)}
                      </td>
                      <td className="text-right">{formatCurrency(proc.avg_deposit)}</td>
                      <td className="text-right text-amber-400">{formatCurrency(proc.processing_fee_estimate)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-white/20 font-bold">
                    <td className="py-3">TOTAL</td>
                    <td className="text-right text-emerald-400">{formatCurrency(data.total_deposits)}</td>
                    <td className="text-right">{data.total_deposit_count}</td>
                    <td className="text-right text-red-400">{formatCurrency(data.total_withdrawals)}</td>
                    <td className="text-right">{data.total_withdrawal_count}</td>
                    <td className={`text-right ${data.total_net_flow >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatCurrency(data.total_net_flow)}
                    </td>
                    <td className="text-right">{formatCurrency(data.avg_deposit_size)}</td>
                    <td className="text-right text-amber-400">
                      {formatCurrency((data.by_processor || []).reduce((sum, p) => sum + p.processing_fee_estimate, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Group View */}
      {viewMode === 'group' && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Breakdown by MT5 Group</CardTitle>
            <CardDescription>Funding activity by trading group for {data.business_date}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b border-white/10">
                    <th className="text-left py-3">Group</th>
                    <th className="text-center py-3">Type</th>
                    <th className="text-right py-3">Clients</th>
                    <th className="text-right py-3">Active</th>
                    <th className="text-right py-3">Deposits</th>
                    <th className="text-right py-3">Withdrawals</th>
                    <th className="text-right py-3">Net Flow</th>
                    <th className="text-right py-3">Fund Rate</th>
                    <th className="text-center py-3">Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_group || []).map((group) => (
                    <tr key={group.group_name} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 font-medium">{group.group_name}</td>
                      <td className="text-center">{getGroupTypeBadge(group.group_type)}</td>
                      <td className="text-right">{group.client_count}</td>
                      <td className="text-right">{group.active_funders}</td>
                      <td className="text-right text-emerald-400">{formatCurrency(group.deposit_amount)}</td>
                      <td className="text-right text-red-400">{formatCurrency(group.withdrawal_amount)}</td>
                      <td className={`text-right font-semibold ${group.net_flow >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {formatCurrency(group.net_flow)}
                      </td>
                      <td className="text-right">{group.funding_rate}%</td>
                      <td className="text-center">{getActivityBadge(group.activity_level)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report Timestamp */}
      <p className="text-xs text-muted-foreground text-right">
        Report generated: {new Date(data.report_generated).toLocaleString()}
      </p>
    </div>
  );
};

// ==================== DIVIDEND IN/OUT TAB ====================

// Default date range values (computed once at module load)
const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0]
  };
};
const defaultDateRange = getDefaultDateRange();

const DividendInOutTab = ({ data, loading, glossary }) => {
  const [viewMode, setViewMode] = useState('summary'); // summary, byType, byGroup
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [dateRange, setDateRange] = useState(defaultDateRange);

  if (loading) return <div className="text-center py-8">Loading dividend data...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  // Get client type color
  const getClientTypeColor = (type) => {
    switch (type) {
      case 'RETAIL': return 'text-cyan-400';
      case 'DIVIDEND': return 'text-amber-400';
      case 'COMPANY_OWN_FUNDS': return 'text-purple-400';
      case 'SPECIAL_DEALS': return 'text-emerald-400';
      default: return 'text-gray-400';
    }
  };

  const getClientTypeBg = (type) => {
    switch (type) {
      case 'RETAIL': return 'bg-cyan-500/10 border-cyan-500/30';
      case 'DIVIDEND': return 'bg-amber-500/10 border-amber-500/30';
      case 'COMPANY_OWN_FUNDS': return 'bg-purple-500/10 border-purple-500/30';
      case 'SPECIAL_DEALS': return 'bg-emerald-500/10 border-emerald-500/30';
      default: return 'bg-gray-500/10 border-gray-500/30';
    }
  };

  return (
    <div className="space-y-4" data-testid="dividend-inout-tab">
      {/* Header with Period Selection */}
      <Card className="glass border-white/10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Period:</span>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm"
                />
                <span className="text-muted-foreground">to</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant={viewMode === 'summary' ? 'default' : 'outline'}
                onClick={() => setViewMode('summary')}
              >
                Summary
              </Button>
              <Button 
                size="sm" 
                variant={viewMode === 'byType' ? 'default' : 'outline'}
                onClick={() => setViewMode('byType')}
              >
                By Client Type
              </Button>
              <Button 
                size="sm" 
                variant={viewMode === 'byGroup' ? 'default' : 'outline'}
                onClick={() => setViewMode('byGroup')}
              >
                By MT5 Group
              </Button>
              <ReportInfo reportKey="dividend-inout" glossary={glossary} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grand Totals */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="glass border-white/10 border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Dividend In</p>
            <p className="text-2xl font-bold text-emerald-400">{formatCurrency(data.total_dividend_in)}</p>
            <p className="text-xs text-muted-foreground">{data.total_dividend_in_count} transactions</p>
            <div className="flex items-center gap-1 mt-1">
              {(data.dividend_in_change_pct || 0) >= 0 ? (
                <ArrowUpRight className="w-3 h-3 text-emerald-400" />
              ) : (
                <ArrowDownRight className="w-3 h-3 text-red-400" />
              )}
              <span className={`text-xs ${(data.dividend_in_change_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {(data.dividend_in_change_pct || 0) >= 0 ? '+' : ''}{(data.dividend_in_change_pct || 0).toFixed(1)}% vs prev period
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10 border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Dividend Out</p>
            <p className="text-2xl font-bold text-red-400">{formatCurrency(data.total_dividend_out)}</p>
            <p className="text-xs text-muted-foreground">{data.total_dividend_out_count} transactions</p>
            <div className="flex items-center gap-1 mt-1">
              {(data.dividend_out_change_pct || 0) >= 0 ? (
                <ArrowUpRight className="w-3 h-3 text-red-400" />
              ) : (
                <ArrowDownRight className="w-3 h-3 text-emerald-400" />
              )}
              <span className={`text-xs ${(data.dividend_out_change_pct || 0) <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {(data.dividend_out_change_pct || 0) >= 0 ? '+' : ''}{(data.dividend_out_change_pct || 0).toFixed(1)}% vs prev period
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className={`glass border-white/10 border-l-4 ${(data.total_net_dividend || 0) >= 0 ? 'border-l-cyan-500' : 'border-l-amber-500'}`}>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Net Dividend</p>
            <p className={`text-2xl font-bold ${(data.total_net_dividend || 0) >= 0 ? 'text-cyan-400' : 'text-amber-400'}`}>
              {formatCurrency(data.total_net_dividend || 0)}
            </p>
            <p className="text-xs text-muted-foreground">In minus Out</p>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Top Group (In)</p>
            <p className="text-lg font-bold text-emerald-400">{data.top_dividend_in_group || '-'}</p>
            <p className="text-xs text-muted-foreground">Highest inflow</p>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Top Type (In)</p>
            <p className="text-lg font-bold text-cyan-400">{data.top_dividend_in_client_type || '-'}</p>
            <p className="text-xs text-muted-foreground">Highest inflow</p>
          </CardContent>
        </Card>
      </div>

      {/* Summary View - By Client Type Cards */}
      {viewMode === 'summary' && data.totals_by_client_type && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Summary by Client Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.totals_by_client_type.map((ct) => (
                <div key={ct.client_type} className={`p-4 rounded-lg border ${getClientTypeBg(ct.client_type)}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`font-semibold ${getClientTypeColor(ct.client_type)}`}>
                      {ct.client_type_label}
                    </span>
                    <Badge variant="outline" className="text-xs">{ct.account_count} accounts</Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">In:</span>
                      <span className="text-emerald-400 font-medium">{formatCurrency(ct.dividend_in)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Out:</span>
                      <span className="text-red-400 font-medium">{formatCurrency(ct.dividend_out)}</span>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-2">
                      <span className="text-muted-foreground">Net:</span>
                      <span className={`font-semibold ${ct.net_dividend >= 0 ? 'text-cyan-400' : 'text-amber-400'}`}>
                        {formatCurrency(ct.net_dividend)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* By Client Type View - Detailed Table */}
      {viewMode === 'byType' && data.totals_by_client_type && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Detailed Breakdown by Client Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-2">Client Type</th>
                    <th className="text-right py-3 px-2">Accounts</th>
                    <th className="text-right py-3 px-2">Dividend In</th>
                    <th className="text-right py-3 px-2"># In</th>
                    <th className="text-right py-3 px-2">Avg In</th>
                    <th className="text-right py-3 px-2">Dividend Out</th>
                    <th className="text-right py-3 px-2"># Out</th>
                    <th className="text-right py-3 px-2">Avg Out</th>
                    <th className="text-right py-3 px-2">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {data.totals_by_client_type.map((ct) => (
                    <tr key={ct.client_type} className="border-b border-white/5 hover:bg-white/5">
                      <td className={`py-3 px-2 font-medium ${getClientTypeColor(ct.client_type)}`}>
                        {ct.client_type_label}
                      </td>
                      <td className="text-right py-3 px-2 text-muted-foreground">{ct.account_count}</td>
                      <td className="text-right py-3 px-2 text-emerald-400">{formatCurrency(ct.dividend_in)}</td>
                      <td className="text-right py-3 px-2 text-muted-foreground">{ct.dividend_in_count}</td>
                      <td className="text-right py-3 px-2 text-muted-foreground">{formatCurrency(ct.avg_dividend_in)}</td>
                      <td className="text-right py-3 px-2 text-red-400">{formatCurrency(ct.dividend_out)}</td>
                      <td className="text-right py-3 px-2 text-muted-foreground">{ct.dividend_out_count}</td>
                      <td className="text-right py-3 px-2 text-muted-foreground">{formatCurrency(ct.avg_dividend_out)}</td>
                      <td className={`text-right py-3 px-2 font-semibold ${ct.net_dividend >= 0 ? 'text-cyan-400' : 'text-amber-400'}`}>
                        {formatCurrency(ct.net_dividend)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-white/20 font-semibold">
                    <td className="py-3 px-2">TOTAL</td>
                    <td className="text-right py-3 px-2">{data.totals_by_client_type.reduce((s, ct) => s + ct.account_count, 0)}</td>
                    <td className="text-right py-3 px-2 text-emerald-400">{formatCurrency(data.total_dividend_in)}</td>
                    <td className="text-right py-3 px-2">{data.total_dividend_in_count}</td>
                    <td className="text-right py-3 px-2">-</td>
                    <td className="text-right py-3 px-2 text-red-400">{formatCurrency(data.total_dividend_out)}</td>
                    <td className="text-right py-3 px-2">{data.total_dividend_out_count}</td>
                    <td className="text-right py-3 px-2">-</td>
                    <td className={`text-right py-3 px-2 ${data.total_net_dividend >= 0 ? 'text-cyan-400' : 'text-amber-400'}`}>
                      {formatCurrency(data.total_net_dividend)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* By MT5 Group View - Expandable */}
      {viewMode === 'byGroup' && data.by_group && (
        <Card className="glass border-white/10">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-cyan-400" />
              Breakdown by MT5 Group
            </CardTitle>
            <CardDescription>Click a group to see client type breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.by_group.map((group) => (
                <div key={group.group_name} className="border border-white/10 rounded-lg overflow-hidden">
                  {/* Group Header - Clickable */}
                  <button
                    onClick={() => setExpandedGroup(expandedGroup === group.group_name ? null : group.group_name)}
                    className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <ChevronRight className={`w-4 h-4 transition-transform ${expandedGroup === group.group_name ? 'rotate-90' : ''}`} />
                      <span className="font-semibold text-cyan-400">{group.group_name}</span>
                      <Badge variant="outline" className="text-xs">{group.total_accounts} accounts</Badge>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-right">
                        <span className="text-muted-foreground mr-2">In:</span>
                        <span className="text-emerald-400 font-medium">{formatCurrency(group.dividend_in)}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-muted-foreground mr-2">Out:</span>
                        <span className="text-red-400 font-medium">{formatCurrency(group.dividend_out)}</span>
                      </div>
                      <div className="text-right min-w-24">
                        <span className="text-muted-foreground mr-2">Net:</span>
                        <span className={`font-semibold ${group.net_dividend >= 0 ? 'text-cyan-400' : 'text-amber-400'}`}>
                          {formatCurrency(group.net_dividend)}
                        </span>
                      </div>
                    </div>
                  </button>
                  
                  {/* Expanded Client Type Breakdown */}
                  {expandedGroup === group.group_name && (
                    <div className="bg-black/30 border-t border-white/10 p-4">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 px-2">Client Type</th>
                            <th className="text-right py-2 px-2">Accounts</th>
                            <th className="text-right py-2 px-2">In</th>
                            <th className="text-right py-2 px-2">Out</th>
                            <th className="text-right py-2 px-2">Net</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.by_client_type.map((ct) => (
                            <tr key={ct.client_type} className="border-b border-white/5">
                              <td className={`py-2 px-2 ${getClientTypeColor(ct.client_type)}`}>
                                {ct.client_type_label}
                              </td>
                              <td className="text-right py-2 px-2 text-muted-foreground">{ct.account_count}</td>
                              <td className="text-right py-2 px-2 text-emerald-400">{formatCurrency(ct.dividend_in)}</td>
                              <td className="text-right py-2 px-2 text-red-400">{formatCurrency(ct.dividend_out)}</td>
                              <td className={`text-right py-2 px-2 font-semibold ${ct.net_dividend >= 0 ? 'text-cyan-400' : 'text-amber-400'}`}>
                                {formatCurrency(ct.net_dividend)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period Info */}
      <p className="text-xs text-center text-muted-foreground">
        Report Period: {data.period_start} to {data.period_end} | Generated: {new Date(data.report_generated).toLocaleString()}
      </p>
    </div>
  );
};

// Valuation Row helper (moved outside component)
const ValuationRow = ({ label, low, mid, high }) => (
  <tr className="border-b border-white/5">
    <td className="py-3 font-medium">{label}</td>
    <td className="text-right text-muted-foreground">{formatCurrency(low)}</td>
    <td className="text-right font-semibold text-cyan-400">{formatCurrency(mid)}</td>
    <td className="text-right text-muted-foreground">{formatCurrency(high)}</td>
  </tr>
);

// Exit Valuation Tab
const ExitValuationTab = ({ data, loading, glossary }) => {
  if (loading) return <div className="text-center py-8">Loading valuation data...</div>;
  if (!data) return <div className="text-center py-8">No data available</div>;

  return (
    <div className="space-y-6">
      {/* Recommended Price */}
      <Card className="glass border-white/10 bg-gradient-to-r from-cyan-500/10 to-emerald-500/10">
        <CardContent className="p-6">
          <div className="flex justify-end mb-2">
            <ReportInfo reportKey="exit-valuation" glossary={glossary} />
          </div>
          <div className="text-center">
            <p className="text-sm uppercase tracking-wider mb-2">Recommended Asking Price</p>
            <p className="text-4xl font-bold text-cyan-400">{formatCurrency(data.recommended_asking_price)}</p>
            <p className="text-sm text-muted-foreground mt-2">Based on multiple valuation methods</p>
          </div>
        </CardContent>
      </Card>

      {/* Valuation Table */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Valuation Analysis</CardTitle>
          <CardDescription>Multiple methods for comprehensive valuation</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b border-white/10">
                  <th className="text-left py-3">Method</th>
                  <th className="text-right py-3">Low</th>
                  <th className="text-right py-3">Mid (Base)</th>
                  <th className="text-right py-3">High</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-white/10 bg-white/5">
                  <td colSpan={4} className="py-2 font-semibold">Revenue Multiple ({data.revenue_multiple_low}x - {data.revenue_multiple_high}x)</td>
                </tr>
                <ValuationRow label={`TTM Revenue: ${formatCurrency(data.ttm_revenue)}`} low={data.valuation_revenue_low} mid={data.valuation_revenue_mid} high={data.valuation_revenue_high} />
                
                <tr className="border-b border-white/10 bg-white/5">
                  <td colSpan={4} className="py-2 font-semibold">EBITDA Multiple ({data.ebitda_multiple_low}x - {data.ebitda_multiple_high}x)</td>
                </tr>
                <ValuationRow label={`TTM EBITDA: ${formatCurrency(data.ttm_ebitda)}`} low={data.valuation_ebitda_low} mid={data.valuation_ebitda_mid} high={data.valuation_ebitda_high} />
                
                <tr className="border-b border-white/10 bg-white/5">
                  <td colSpan={4} className="py-2 font-semibold">AUM Multiple ({(data.aum_multiple_low * 100).toFixed(0)}% - {(data.aum_multiple_high * 100).toFixed(0)}%)</td>
                </tr>
                <ValuationRow label={`Current AUM: ${formatCurrency(data.current_aum)}`} low={data.valuation_aum_low} mid={data.valuation_aum_mid} high={data.valuation_aum_high} />
                
                <tr className="border-b border-white/10 bg-white/5">
                  <td colSpan={4} className="py-2 font-semibold">Per-Client Value (${data.value_per_client_low?.toLocaleString()} - ${data.value_per_client_high?.toLocaleString()})</td>
                </tr>
                <ValuationRow label={`Active Clients: ${data.active_clients}`} low={data.valuation_client_low} mid={data.valuation_client_mid} high={data.valuation_client_high} />
                
                <tr className="border-t-2 border-white/20 font-bold">
                  <td className="py-3">AVERAGE VALUATION</td>
                  <td className="text-right">{formatCurrency(data.average_valuation_low)}</td>
                  <td className="text-right text-cyan-400">{formatCurrency(data.average_valuation_mid)}</td>
                  <td className="text-right">{formatCurrency(data.average_valuation_high)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Comparable Deals */}
      <Card className="glass border-white/10">
        <CardHeader><CardTitle className="text-lg">Comparable Transactions</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b border-white/10">
                  <th className="text-left py-2">Company</th>
                  <th className="text-center py-2">Year</th>
                  <th className="text-right py-2">Revenue Multiple</th>
                  <th className="text-right py-2">Deal Size</th>
                </tr>
              </thead>
              <tbody>
                {(data.comparable_deals || []).map((deal, idx) => (
                  <tr key={idx} className="border-b border-white/5">
                    <td className="py-2">{deal.company}</td>
                    <td className="text-center">{deal.year}</td>
                    <td className="text-right">{deal.revenue_multiple}x</td>
                    <td className="text-right font-semibold">{formatCurrency(deal.deal_size)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Report Navigation Config

// Export all tab components
export {
  MetricCard,
  ExecutiveSummaryTab,
  FinancialStatementTab,
  BalanceSheetTab,
  ClientAnalyticsTab,
  TradingAnalyticsTab,
  BookAnalysisTab,
  MarginCallsTab,
  RiskReportTab,
  InvestorReportTab,
  PnLBySymbolTab,
  ClientProfitabilityTab,
  CommissionAnalyticsTab,
  DepositFlowTab,
  HedgingEfficiencyTab,
  RegulatoryCapitalTab,
  SuspiciousActivityTab,
  ExecutionQualityTab,
  DailyFundingTab,
  DividendInOutTab,
  ExitValuationTab
};
